import { el, renderSymbol } from '@/utils/dom';
import { formatEuro } from '@/utils/format';

export interface HourlyCostModalOpts {
  /** Valore mensile iniziale (opzionale). */
  currentMonthly?: number;
  /** Callback invocato quando l'utente conferma. */
  onApply: (hourlyCost: number) => void;
  /** Callback invocato quando l'utente annulla o chiude. */
  onCancel?: () => void;
}

/**
 * Modale per calcolare il costo orario (C_h) a partire dallo stipendio mensile.
 *
 * Formula: C_h = C_m / 8 (h/giorno) / 26 (giorni/mese)
 *
 * I parametri 8 e 26 sono modificabili dall'utente (default: 8 ore/giorno,
 * 26 giorni/mese = 208 ore/mese). Alla chiusura tramite "Applica",
 * il valore calcolato viene inviato al callback `onApply`.
 */
export class HourlyCostModal {
  private opts: HourlyCostModalOpts;
  private root?: HTMLDivElement;

  constructor(opts: HourlyCostModalOpts) {
    this.opts = opts;
  }

  open(): void {
    if (this.root) return;
    this.root = el('div', {
      className: 'modal-backdrop',
      role: 'dialog',
      aria: { modal: 'true', labelledby: 'hc-modal-title' },
    }) as HTMLDivElement;

    // Chiude cliccando sul backdrop
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close(/* cancelled */ true);
    });

    // Chiude con ESC
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close(true);
        document.removeEventListener('keydown', onKey);
      }
    };
    document.addEventListener('keydown', onKey);

    this.root.appendChild(this.renderPanel());
    document.body.appendChild(this.root);
    document.body.style.overflow = 'hidden';

    // Focus iniziale sull'input principale
    queueMicrotask(() => {
      const first = this.root?.querySelector<HTMLInputElement>('#hc-monthly');
      first?.focus();
      first?.select();
    });
  }

  private renderPanel(): HTMLElement {
    const monthlyInit = this.opts.currentMonthly ?? 2800;
    const hoursPerDayInit = 8;
    const daysPerMonthInit = 26;

    return el('div', { className: 'modal-panel' }, [
      // Header
      el('div', { className: 'flex items-start justify-between mb-4' }, [
        el('div', {}, [
          el('h2', { className: 'text-lg font-semibold text-slate-900', attrs: { id: 'hc-modal-title' }, html: 'Calcola il costo orario (C<sub>h</sub>)' }),
          el('p', { className: 'text-sm text-slate-500 mt-0.5', text: 'A partire dallo stipendio mensile lordo (comprensivo di oneri sociali).' }),
        ]),
        el('button', {
          className: 'btn-ghost !p-1.5 !rounded-full',
          attrs: { type: 'button', 'aria-label': 'Chiudi' },
          onClick: () => this.close(true),
        }, [el('span', { className: 'text-xl leading-none', html: '<i class="fas fa-x"></i>' })]),
      ]),

      // Formula
      el('div', { className: 'rounded-lg bg-brand-50 border border-brand-100 p-3 mb-4' }, [
        el('p', { className: 'text-xs text-brand-800 font-medium mb-1', text: 'Formula' }),
        el('p', { className: 'font-mono text-sm text-brand-900', html: 'C<sub>h</sub> = C<sub>m</sub> / ore_per_giorno / giorni_per_mese' }),
      ]),

      // Inputs
      el('div', { className: 'space-y-3' }, [
        this.numberField({
          id: 'hc-monthly',
          label: 'Stipendio mensile ( C_m )',
          unit: '€',
          value: monthlyInit,
          min: 0,
          step: 50,
        }),
        el('div', { className: 'grid grid-cols-2 gap-3' }, [
          this.numberField({
            id: 'hc-hours',
            label: 'Ore/giorno',
            unit: 'h',
            value: hoursPerDayInit,
            min: 1,
            max: 24,
            step: 1,
          }),
          this.numberField({
            id: 'hc-days',
            label: 'Giorni/mese',
            unit: 'gg',
            value: daysPerMonthInit,
            min: 1,
            max: 31,
            step: 1,
          }),
        ]),
      ]),

      // Risultato live
      el('div', { className: 'mt-5 p-4 rounded-lg bg-slate-900 text-white flex items-center justify-between' }, [
        el('div', {}, [
          el('p', { className: 'text-xs uppercase tracking-wide text-slate-300', text: 'Costo orario calcolato' }),
          el('p', { className: 'text-2xl font-bold tabular-nums', attrs: { id: 'hc-result' }, text: formatEuro(monthlyInit / hoursPerDayInit / daysPerMonthInit, 2) }),
        ]),
        el('p', { className: 'text-xs text-slate-400 text-right', text: 'Per ora\nlavorata' }),
      ]),

      // Actions
      el('div', { className: 'mt-5 flex items-center justify-end gap-2' }, [
        el('button', {
          className: 'btn-secondary',
          attrs: { type: 'button' },
          onClick: () => this.close(true),
        }, [el('span', { text: 'Annulla' })]),
        el('button', {
          className: 'btn-primary',
          attrs: { type: 'button' },
          onClick: () => {
            const hourly = this.computeCurrent();
            this.opts.onApply(hourly);
            this.close(false);
          },
        }, [el('span', { html: 'Applica a C<sub>h</sub>' })]),
      ]),

      // Live update handler
      this.attachLiveUpdate(),
    ]);
  }

  private numberField(o: { id: string; label: string; unit: string; value: number; min: number; max?: number; step: number }): HTMLElement {
    const wrap = el('div', {}, [
      el('label', { className: 'field-label', attrs: { for: o.id }, ...renderSymbol(o.label) }),
      el('div', { className: 'flex items-stretch gap-1' }, [
        el('input', {
          className: 'input-base tabular-nums',
          attrs: {
            id: o.id,
            type: 'number',
            inputmode: 'decimal',
            min: String(o.min),
            max: o.max !== undefined ? String(o.max) : '',
            step: String(o.step),
            value: String(o.value),
            'aria-label': o.label,
          },
          onInput: () => this.refreshResult(),
        }),
        el('span', { className: 'inline-flex items-center px-3 rounded-r-lg bg-slate-100 border border-l-0 border-slate-300 text-xs text-slate-600', text: o.unit }),
      ]),
    ]);
    return wrap;
  }

  /** Elemento "segnaposto" che attacca il listener globale sugli input. */
  private attachLiveUpdate(): HTMLElement {
    // Render: aggancia listeners differito
    queueMicrotask(() => this.refreshResult());
    return el('div', { className: 'hidden' });
  }

  private computeCurrent(): number {
    const monthly = this.num('hc-monthly', 2800);
    const hours = this.num('hc-hours', 8);
    const days = this.num('hc-days', 26);
    if (hours <= 0 || days <= 0) return 0;

    let calc = Math.round((monthly / hours / days) * 10).toString();

    calc = parseInt(calc.charAt(calc.length - 1), 10) > 4 ? calc.slice(0, calc.length - 1) + '5' : calc.slice(0, calc.length - 1) + '0';
    return parseInt(calc, 10) / 10;
  }

  private refreshResult(): void {
    const result = document.getElementById('hc-result');
    if (result) result.textContent = formatEuro(this.computeCurrent(), 2);
  }

  private num(id: string, fallback: number): number {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return fallback;
    const v = Number(el.value);
    return Number.isFinite(v) ? v : fallback;
  }

  private close(cancelled: boolean): void {
    if (!this.root) return;
    document.body.style.overflow = '';
    this.root.remove();
    this.root = undefined;
    if (cancelled) this.opts.onCancel?.();
  }
}
